import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useSessionStore } from "@/stores";
import type IForeignItem from "@/models/responses/IForeignItem";
import SyncForeignItemsRequest from "@/models/requests/SyncForeignItemsRequest";

export const useForeignItemsStore = defineStore("foreignItems", {
  state: () => ({
    error: "" as any,
    data: [] as IForeignItem[],
    loadedSiteId: 0,
  }),
  actions: {
    resetLoaded() {
      this.loadedSiteId = 0;
    },
    async fetchAll(force = false) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedSiteId: selectedSiteId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loadedSiteId === selectedSiteId.value) return;

      this.error = "";
      await api
        .get<IForeignItem[]>(`v1.0/foreign-items?siteId=${selectedSiteId.value}`)
        .then((response) => {
          this.data = response.data;
          this.loadedSiteId = selectedSiteId.value;
        })
        .catch((error) => (this.error = error));
    },
    async sync(req: SyncForeignItemsRequest, onfulfilled: (data: IForeignItem[]) => void, onrejected: (reason: any) => void | PromiseLike<void>) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .post<IForeignItem[]>(`v1.0/foreign-items`, req)
        .then((response) => {
          // remove items that are equal to the requested siteId, because the response contains all items for the site
          this.data = this.data.filter((x) => x.siteId !== req.siteId);
          response.data.forEach((item) => {
            const index = this.data.findIndex((x) => x.id === item.id);
            if (index === -1) this.data.push(item);
            else this.data[index] = item;
          });
          onfulfilled(response.data);
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
  },
  getters: {
    getById: (state) => (id: number) => state.data.find((foreignItem) => foreignItem.id === id),
    getByItemId: (state) => (itemId: number) => state.data.find((foreignItem) => foreignItem.itemId === itemId),
  },
});
